import { CButton, CBadge, CRow, CCol } from "@coreui/react";
import { useFormContext } from "react-hook-form";
import { SectionCard } from "../__sections";
import { STATUS_OPTIONS_MAP, VEHICLE_TYPES } from "../../../helpers/constants";
import {
  formatDate,
  formatMinutes,
  langFlag,
  langLabel,
} from "../../../helpers/general";
import { colors } from "../../../config/theme";
import {
  GeoMode,
  LatLng,
  PaymentOptions,
  PlacePoint,
} from "../../../helpers/enums";
import { GalleryItem } from "./step7Gallery";
import { CreditCardIcon } from "../../../components/icons/CreditCardIcon";
import { Wallet } from "lucide-react";

interface Step8Props {
  goToStep: (step: number) => void;
  galleryItems: GalleryItem[];
  pickupGeoMode: GeoMode;
  pickupPlaces: PlacePoint[];
  pickupPolygon: LatLng[];
  dropoffSameAsPickup: boolean;
  dropoffGeoMode: GeoMode;
  dropoffPlaces: PlacePoint[];
  dropoffPolygon: LatLng[];
}

const WEEKDAY_LABELS: Record<string, string> = {
  mon: "Lun",
  tue: "Mar",
  wed: "Mer",
  thu: "Jeu",
  fri: "Ven",
  sat: "Sam",
  sun: "Dim",
};

/** Small "edit this section" link, consistent across every card. */
function EditLink({ onClick }: { onClick: () => void }) {
  return (
    <CButton
      size="sm"
      variant="ghost"
      className="p-0"
      style={{ color: colors.primary, fontWeight: 600 }}
      onClick={onClick}
    >
      Modifier →
    </CButton>
  );
}

function CardHeader({ title, onEdit }: { title: string; onEdit: () => void }) {
  return (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <span
        className="fw-semibold"
        style={{ fontSize: "0.85rem", color: "#555", letterSpacing: "0.02em" }}
      >
        {title.toUpperCase()}
      </span>
      <EditLink onClick={onEdit} />
    </div>
  );
}

const itemCardStyle: React.CSSProperties = {
  border: "1px solid #f0f0f0",
  borderRadius: "10px",
  backgroundColor: "#fcfcfc",
  padding: "0.75rem 0.9rem",
};

const emptyStateStyle: React.CSSProperties = {
  border: "1px dashed #ddd",
  borderRadius: "10px",
  color: "#999",
};

export default function Review({
  goToStep,
  galleryItems,
  pickupGeoMode,
  pickupPlaces,
  pickupPolygon,
  dropoffSameAsPickup,
  dropoffGeoMode,
  dropoffPlaces,
  dropoffPolygon,
}: Step8Props) {
  const { watch } = useFormContext();
  const watchedTitle = watch("title");
  const watchedStatus = watch("status");
  const watchedTranslations = watch("translations") || [];
  const watchedItineraries = watch("itineraries") || [];
  const watchedPickupDropoff = watch("pickupDropoff");
  const watchedSchedules = watch("schedules") || [];
  const watchedOptions = watch("options") || [];
  const watchedPaymentMethods = watch("paymentMethods") || [];

  const currentStatus = STATUS_OPTIONS_MAP.find(
    (s) => s.value === watchedStatus,
  );

  const imageCount = galleryItems.filter((g) => g.type !== "VIDEO").length;
  const videoCount = galleryItems.filter((g) => g.type === "VIDEO").length;

  // ── Stripe Euro Price Validation ────────────────────────────────
  const hasStripe = watchedPaymentMethods.includes(PaymentOptions.STRIPE);
  const missingEuroPriceSchedules = watchedSchedules.filter((s: any) => {
    if (s.pricingType === "flat") {
      return (
        s.flatPriceInEuro === undefined ||
        s.flatPriceInEuro === "" ||
        s.flatPriceInEuro === null
      );
    }
    if (s.pricingType === "per_age_group") {
      return s.ageGroups?.some(
        (ag: any) =>
          ag.priceInEuro === undefined ||
          ag.priceInEuro === "" ||
          ag.priceInEuro === null,
      );
    }
    return false;
  });
  const showEuroPriceWarning =
    hasStripe && missingEuroPriceSchedules.length > 0;
  // ────────────────────────────────────────────────────────────────

  const PAYMENT_METHOD_LABELS: Record<
    string,
    { label: string; icon: React.ReactNode }
  > = {
    [PaymentOptions.CREDIT_CARD]: {
      label: "Carte bancaire",
      icon: <CreditCardIcon width={14} height={14} color="#000000" />,
    },
    [PaymentOptions.STRIPE]: {
      label: "Paiement en Euro (Stripe)",
      icon: <CreditCardIcon width={14} height={14} color="#003399" />,
    },
    [PaymentOptions.WAFACASH]: {
      label: "Wafacash",
      icon: null,
    },
    [PaymentOptions.USER_BALANCE]: {
      label: "Solde du portefeuille",
      icon: <Wallet size={14} />,
    },
    [PaymentOptions.IN_PLACE]: {
      label: "Paiement sur place",
      icon: null,
    },
  };

  return (
    <div className="animate-fade-in d-flex flex-column gap-3">
      {/* ── Global Stripe Warning ──────────────────────────────── */}
      {showEuroPriceWarning && (
        <div
          className="p-3 d-flex align-items-start gap-3"
          style={{
            backgroundColor: "#fff5f5",
            border: "1px solid #ffcccc",
            color: "#d32f2f",
            borderRadius: 10,
            fontWeight: 500,
          }}
        >
          <span style={{ flexShrink: 0, fontSize: "1.3rem" }}>⚠️</span>
          <div className="flex-grow-1">
            <div className="fw-semibold mb-1" style={{ fontSize: "0.95rem" }}>
              Prix en Euros manquants pour Stripe
            </div>
            <div className="small" style={{ color: "#b71c1c" }}>
              Le paiement par <strong>Stripe</strong> est activé, mais les prix
              en Euros ne sont pas définis pour certains calendriers (
              <strong>
                {missingEuroPriceSchedules
                  .map((s : any) => s.name || "sans nom")
                  .join(", ")}
              </strong>
              ). Veuillez définir les <strong>prix en Euros</strong> pour
              pouvoir accepter les paiements en ligne.
            </div>
            <CButton
              size="sm"
              className="mt-2"
              style={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                border: "none",
                fontWeight: 600,
              }}
              onClick={() => {
                document
                  .getElementById("section-schedules")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              Voir les calendriers concernés →
            </CButton>
          </div>
        </div>
      )}

      {/* ── Basic info ─────────────────────────────────────────── */}
      <SectionCard title="Informations de base">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="small text-muted mb-1">Titre</div>
            <div className="fw-semibold" style={{ fontSize: "1.05rem" }}>
              {watchedTitle || "—"}
            </div>
          </div>
          {currentStatus && (
            <CBadge
              shape="rounded-pill"
              style={{
                backgroundColor: currentStatus.color,
                color: "#fff",
                padding: "0.4rem 0.8rem",
                fontWeight: 600,
              }}
            >
              {currentStatus.label}
            </CBadge>
          )}
        </div>
        <div className="mt-2">
          <EditLink onClick={() => goToStep(1)} />
        </div>
      </SectionCard>

      {/* ── Pickup & Dropoff ───────────────────────────────────── */}
      <SectionCard title="Pickup & Dropoff">
        <CardHeader title="Détails" onEdit={() => goToStep(2)} />
        <CRow>
          <CCol md={6}>
            <div style={itemCardStyle} className="h-100">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="small text-muted">Pickup</span>
                {watchedPickupDropoff?.isPickupAvailable ? (
                  <CBadge
                    shape="rounded-pill"
                    style={{
                      backgroundColor: `${colors.primary}18`,
                      color: colors.primary,
                    }}
                  >
                    Proposé
                  </CBadge>
                ) : (
                  <CBadge
                    shape="rounded-pill"
                    style={{ backgroundColor: "#eee", color: "#999" }}
                  >
                    Non proposé
                  </CBadge>
                )}
              </div>
              {watchedPickupDropoff?.isPickupAvailable && (
                <>
                  <div className="fw-semibold">
                    {pickupGeoMode === "zones"
                      ? `Zone (${pickupPolygon.length} sommet(s))`
                      : `${pickupPlaces.length} point(s)`}
                  </div>
                  {watchedPickupDropoff?.transportation && (
                    <div className="small text-muted">
                      {VEHICLE_TYPES.find(
                        (v) => v.value === watchedPickupDropoff.transportation,
                      )?.label || watchedPickupDropoff.transportation}
                    </div>
                  )}
                  {watchedPickupDropoff?.pickupDescription && (
                    <div className="small text-muted mt-1 fst-italic">
                      "{watchedPickupDropoff.pickupDescription}"
                    </div>
                  )}
                </>
              )}
            </div>
          </CCol>
          <CCol md={6}>
            <div style={itemCardStyle} className="h-100">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="small text-muted">Dropoff</span>
                {watchedPickupDropoff?.isDropoffAvailable ? (
                  <CBadge
                    shape="rounded-pill"
                    style={{
                      backgroundColor: `${colors.primary}18`,
                      color: colors.primary,
                    }}
                  >
                    Proposé
                  </CBadge>
                ) : (
                  <CBadge
                    shape="rounded-pill"
                    style={{ backgroundColor: "#eee", color: "#999" }}
                  >
                    Non proposé
                  </CBadge>
                )}
              </div>
              {watchedPickupDropoff?.isDropoffAvailable &&
                (dropoffSameAsPickup ? (
                  <div className="fw-semibold">Identique au pickup</div>
                ) : (
                  <>
                    <div className="fw-semibold">
                      {dropoffGeoMode === "zones"
                        ? `Zone (${dropoffPolygon.length} sommet(s))`
                        : `${dropoffPlaces.length} point(s)`}
                    </div>
                    {watchedPickupDropoff?.dropoffDescription && (
                      <div className="small text-muted mt-1 fst-italic">
                        "{watchedPickupDropoff.dropoffDescription}"
                      </div>
                    )}
                  </>
                ))}
            </div>
          </CCol>
        </CRow>
      </SectionCard>

      {/* ── Translations ───────────────────────────────────────── */}
      <SectionCard title={`Traductions (${watchedTranslations.length})`}>
        <CardHeader title="Contenu par langue" onEdit={() => goToStep(1)} />
        {watchedTranslations.length ? (
          <div className="d-flex flex-column gap-2">
            {watchedTranslations.map((t: any, i: number) => (
              <div key={i} style={itemCardStyle}>
                <div className="d-flex align-items-start gap-2">
                  <span style={{ fontSize: "1.1rem" }}>{langFlag(t.lang)}</span>
                  <div className="flex-grow-1" style={{ minWidth: 0 }}>
                    <div className="fw-semibold">
                      {t.title || "(sans titre)"}
                    </div>
                    <div className="small text-muted">{langLabel(t.lang)}</div>
                    {t.shortDescription && (
                      <div
                        className="small mt-1 text-truncate"
                        style={{ color: "#666" }}
                      >
                        {t.shortDescription}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-3 small" style={emptyStateStyle}>
            Aucune traduction
          </div>
        )}
      </SectionCard>

      {/* ── Itinerary ──────────────────────────────────────────── */}
      <SectionCard title={`Itinéraire (${watchedItineraries.length} étape(s))`}>
        <CardHeader title="Déroulé" onEdit={() => goToStep(3)} />
        {watchedItineraries.length ? (
          <div className="d-flex flex-column gap-2">
            {watchedItineraries.map((it: any, i: number) => (
              <div
                key={i}
                style={itemCardStyle}
                className="d-flex align-items-center gap-3"
              >
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    backgroundColor: `${colors.primary}18`,
                    color: colors.primary,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  }}
                >
                  {i + 1}
                </div>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="fw-semibold text-truncate">
                    {it.title || "(sans titre)"}
                  </div>
                  <div className="small text-muted">
                    {it.type === "TRANSPORTATION" ? "Transport" : "Activité"}
                    {it.duration ? ` · ${it.duration}` : ""}
                  </div>
                </div>
                {it.isOptional && (
                  <CBadge
                    shape="rounded-pill"
                    style={{ backgroundColor: "#eee", color: "#888" }}
                  >
                    Optionnel
                  </CBadge>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-3 small" style={emptyStateStyle}>
            Aucune étape
          </div>
        )}
      </SectionCard>

      {/* ── Schedules ──────────────────────────────────────────── */}
      <div id="section-schedules">
        <SectionCard
          title={`Disponibilités (${watchedSchedules.length} calendrier(s))`}
        >
          <CardHeader title="Calendriers" onEdit={() => goToStep(4)} />
          {watchedSchedules.length ? (
            <div className="d-flex flex-column gap-2">
              {watchedSchedules.map((s: any, i: number) => {
                const activeDays = Object.entries(s.weeklySlots || {}).filter(
                  ([, slots]: any) => Array.isArray(slots) && slots.length > 0,
                );
                return (
                  <div key={i} style={itemCardStyle}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="fw-semibold">
                          {s.name || "(sans nom)"}
                        </div>
                        <div className="small text-muted">
                          {formatDate(s.startDate)
                            ? `À partir du ${formatDate(s.startDate)}`
                            : "Date non définie"}
                          {s.endDate ? ` → ${formatDate(s.endDate)}` : ""}
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {s.isActive === false && (
                          <CBadge
                            shape="rounded-pill"
                            style={{ backgroundColor: "#eee", color: "#999" }}
                          >
                            Inactif
                          </CBadge>
                        )}
                        <CBadge
                          shape="rounded-pill"
                          style={{
                            backgroundColor: `${colors.yellow}22`,
                            color: "#9a7500",
                          }}
                        >
                          {s.pricingType === "flat"
                            ? `${s.flatPrice ?? "—"}`
                            : "par tranche d'âge"}
                        </CBadge>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {activeDays.length ? (
                        activeDays.map(([day]: any) => (
                          <span
                            key={day}
                            className="small"
                            style={{
                              backgroundColor: "#fffdf5",
                              border: `1px solid ${colors.yellow}33`,
                              borderRadius: 6,
                              padding: "0.15rem 0.5rem",
                            }}
                          >
                            {WEEKDAY_LABELS[day] || day}
                          </span>
                        ))
                      ) : (
                        <span className="small text-muted">
                          Aucun créneau défini
                        </span>
                      )}
                    </div>
                    {(s.minParticipants || s.maxParticipants) && (
                      <div className="small text-muted mt-2">
                        Participants: {s.minParticipants ?? 1}
                        {s.maxParticipants ? ` – ${s.maxParticipants}` : "+"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-3 small" style={emptyStateStyle}>
              Aucun calendrier
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── Options ────────────────────────────────────────────── */}
      <SectionCard title={`Options (${watchedOptions.length})`}>
        <CardHeader title="Variantes réservables" onEdit={() => goToStep(5)} />
        {watchedOptions.length ? (
          <div className="d-flex flex-column gap-2">
            {watchedOptions.map((o: any, i: number) => {
              const scheduleLabel =
                o.scheduleMode === "default"
                  ? "Calendrier par défaut"
                  : o.scheduleMode === "existing"
                    ? "Calendrier réutilisé"
                    : "Calendrier personnalisé";
              const duration = formatMinutes(o.durationMinutes);
              const freeCancel = formatMinutes(o.freeCancelThreshold);
              const booking = formatMinutes(o.bookingThreshold);

              return (
                <div key={i} style={itemCardStyle}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="fw-semibold">
                      {o.title || "(sans titre)"}
                    </div>
                    <CBadge
                      shape="rounded-pill"
                      style={{
                        backgroundColor: `${colors.primary}18`,
                        color: colors.primary,
                      }}
                    >
                      {scheduleLabel}
                    </CBadge>
                  </div>
                  {o.description && (
                    <div className="small text-muted mt-1">{o.description}</div>
                  )}
                  <div className="d-flex flex-wrap gap-3 mt-2 small text-muted">
                    {duration && <span>⏱ {duration}</span>}
                    {freeCancel && (
                      <span>✓ Annulation gratuite avant {freeCancel}</span>
                    )}
                    {booking && <span>🔒 Fermeture avant {booking}</span>}
                  </div>
                  {(o.meta?.wheelchairAccessible || o.meta?.isPrivate) && (
                    <div className="d-flex gap-2 mt-2">
                      {o.meta?.wheelchairAccessible && (
                        <CBadge
                          shape="rounded-pill"
                          style={{ backgroundColor: "#eee", color: "#666" }}
                        >
                          Accessible fauteuil roulant
                        </CBadge>
                      )}
                      {o.meta?.isPrivate && (
                        <CBadge
                          shape="rounded-pill"
                          style={{ backgroundColor: "#eee", color: "#666" }}
                        >
                          Privée
                        </CBadge>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-3 small" style={emptyStateStyle}>
            Aucune option
          </div>
        )}
      </SectionCard>

      {/* ── Payment ────────────────────────────────────────────── */}
      <SectionCard title={`Paiement (${watchedPaymentMethods.length})`}>
        <CardHeader
          title="Méthodes de paiement acceptées"
          onEdit={() => goToStep(6)}
        />
        {watchedPaymentMethods.length ? (
          <div className="d-flex flex-wrap gap-2">
            {watchedPaymentMethods.map((method: string) => {
              const detail = PAYMENT_METHOD_LABELS[method];
              return (
                <div
                  key={method}
                  style={itemCardStyle}
                  className="d-flex align-items-center gap-2"
                >
                  <span
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 20, height: 20 }}
                  >
                    {detail?.icon}
                  </span>
                  <span className="fw-semibold small">
                    {detail?.label || method}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-3 small" style={emptyStateStyle}>
            Aucune méthode de paiement
          </div>
        )}
      </SectionCard>

      {/* ── Gallery ────────────────────────────────────────────── */}
      <SectionCard title={`Galerie (${galleryItems.length} élément(s))`}>
        <CardHeader
          title={`${imageCount} photo(s) · ${videoCount} vidéo(s)`}
          onEdit={() => goToStep(7)}
        />
        {galleryItems.length ? (
          <div className="d-flex flex-wrap gap-2">
            {galleryItems.slice(0, 10).map((item) => (
              <div
                key={item.id}
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 10,
                  overflow: "hidden",
                  border: "1px solid #ececec",
                  position: "relative",
                }}
              >
                {item.type === "VIDEO" ? (
                  <video
                    src={item.url}
                    className="w-100 h-100"
                    style={{ objectFit: "cover" }}
                    muted
                  />
                ) : (
                  <img
                    src={item.url}
                    alt=""
                    className="w-100 h-100"
                    style={{ objectFit: "cover" }}
                  />
                )}
              </div>
            ))}
            {galleryItems.length > 10 && (
              <div
                className="d-flex align-items-center justify-content-center small fw-semibold"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 10,
                  backgroundColor: "#f4f4f4",
                  color: "#666",
                }}
              >
                +{galleryItems.length - 10}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-3 small" style={emptyStateStyle}>
            Aucun fichier
          </div>
        )}
      </SectionCard>

      <div
        className="small text-muted mt-1 p-3"
        style={{
          border: "1px dashed #ddd",
          borderRadius: "10px",
          backgroundColor: "#fafafa",
        }}
      >
        ℹ️ Vérifiez les informations ci-dessus avant de soumettre. Vous pourrez
        modifier l'activité plus tard.
      </div>
    </div>
  );
}
